/**
 * Constants for Caster Mold Tracking
 * Must match seed-caster.sql + migrate-billet-size-to-strands.sql values in database
 *
 * ID Ranges:
 *   catPartTypes: 1000-1099 (parts), 1100-1199 (spares arrays)
 *   catParts:     1000-1099 (containers), 1100-1199 (spares arrays)
 */

// ============================================
// Status Codes (shared with EAF modules)
// ============================================

export {
  STATUS_CODE,
  isValidStatusCode,
} from "../../../../shared/models";
export type { StatusCode } from "../../../../shared/models";

// ============================================
// Part Type IDs (from catPartTypes table)
// ============================================

export const PART_TYPE_IDS = {
  // Container & structure types (1000-1013)
  CASTER_ONLINE: 1000,
  CASTER_OFFLINE: 1001,
  STRAND: 1002,
  MOLD: 1003,

  // Group types (non-replaceable, hold children)
  FLANGES: 1010,
  COLLARS: 1011,
  SEALS: 1012,
  FOOT_ROLL_ASSEMBLY: 1013,

  // Standalone replaceable components (direct Mold children)
  COPPER_MOLD_TUBE: 1020,
  MOLD_BODY: 1021,
  OIL_DISTRIBUTION_GASKET: 1022,
  LEVEL_CONTROL: 1023,
  OIL_CONNECTOR: 1024,
  WATER_JACKET_FLUME: 1025,

  // Flange components
  UPPER_FLANGE: 1030,
  LOW_UPPER_FLANGE: 1031,
  LOWER_FLANGE: 1032,
  CLOSING_FLANGE: 1033,
  WATER_JACKET_FIXING_FLANGE: 1034,

  // Collar components
  UPPER_COLLAR: 1040,
  LOWER_COLLAR: 1041,

  // Seal components
  UPPER_SEAL: 1050,
  COPPER_TUBE_SEAL: 1051,
  WATER_JACKET_SEAL: 1052,
  LOWER_INTERNAL_SEAL: 1053,
  LOWER_EXTERNAL_SEAL: 1054,
  OIL_CONNECTOR_ORING: 1055,

  // Foot Roll Assembly components
  FOOT_ROLL: 1060,
  ROLL_SHAFT: 1061,
  ROLLER_SUPPORT: 1062,
  SHAFT_KEEPER: 1063,
  Z1_SPRAY: 1064,
  SPRAY_NOZZLE: 1065,

  // Spares array types (1100-1165)
  MOLD_SPARES: 1100,
  COPPER_MOLD_TUBE_SPARES: 1120,
  MOLD_BODY_SPARES: 1121,
  OIL_DISTRIBUTION_GASKET_SPARES: 1122,
  LEVEL_CONTROL_SPARES: 1123,
  OIL_CONNECTOR_SPARES: 1124,
  WATER_JACKET_FLUME_SPARES: 1125,
  UPPER_FLANGE_SPARES: 1130,
  LOW_UPPER_FLANGE_SPARES: 1131,
  LOWER_FLANGE_SPARES: 1132,
  CLOSING_FLANGE_SPARES: 1133,
  WATER_JACKET_FIXING_FLANGE_SPARES: 1134,
  UPPER_COLLAR_SPARES: 1140,
  LOWER_COLLAR_SPARES: 1141,
  UPPER_SEAL_SPARES: 1150,
  COPPER_TUBE_SEAL_SPARES: 1151,
  WATER_JACKET_SEAL_SPARES: 1152,
  LOWER_INTERNAL_SEAL_SPARES: 1153,
  LOWER_EXTERNAL_SEAL_SPARES: 1154,
  OIL_CONNECTOR_ORING_SPARES: 1155,
  FOOT_ROLL_SPARES: 1160,
  ROLL_SHAFT_SPARES: 1161,
  ROLLER_SUPPORT_SPARES: 1162,
  SHAFT_KEEPER_SPARES: 1163,
  Z1_SPRAY_SPARES: 1164,
  SPRAY_NOZZLE_SPARES: 1165,
} as const satisfies Record<string, number>;

/** Type for part type ID values */
export type PartTypeId = (typeof PART_TYPE_IDS)[keyof typeof PART_TYPE_IDS];

// ============================================
// Part IDs (from catParts table)
// ============================================

export const PART_IDS = {
  // Structure containers
  CASTER_ONLINE: 1000,
  CASTER_OFFLINE: 1001,

  // Strand containers (5 strands of the caster)
  STRAND_1: 1002,
  STRAND_2: 1003,
  STRAND_3: 1004,
  STRAND_4: 1005,
  STRAND_5: 1006,

  // Spares array instances
  MOLD_SPARES: 1100,
  COPPER_MOLD_TUBE_SPARES: 1120,
  MOLD_BODY_SPARES: 1121,
  OIL_DISTRIBUTION_GASKET_SPARES: 1122,
  LEVEL_CONTROL_SPARES: 1123,
  OIL_CONNECTOR_SPARES: 1124,
  WATER_JACKET_FLUME_SPARES: 1125,
  UPPER_FLANGE_SPARES: 1130,
  LOW_UPPER_FLANGE_SPARES: 1131,
  LOWER_FLANGE_SPARES: 1132,
  CLOSING_FLANGE_SPARES: 1133,
  WATER_JACKET_FIXING_FLANGE_SPARES: 1134,
  UPPER_COLLAR_SPARES: 1140,
  LOWER_COLLAR_SPARES: 1141,
  UPPER_SEAL_SPARES: 1150,
  COPPER_TUBE_SEAL_SPARES: 1151,
  WATER_JACKET_SEAL_SPARES: 1152,
  LOWER_INTERNAL_SEAL_SPARES: 1153,
  LOWER_EXTERNAL_SEAL_SPARES: 1154,
  OIL_CONNECTOR_ORING_SPARES: 1155,
  FOOT_ROLL_SPARES: 1160,
  ROLL_SHAFT_SPARES: 1161,
  ROLLER_SUPPORT_SPARES: 1162,
  SHAFT_KEEPER_SPARES: 1163,
  Z1_SPRAY_SPARES: 1164,
  SPRAY_NOZZLE_SPARES: 1165,
} as const satisfies Record<string, number>;

/** Type for part ID values */
export type PartId = (typeof PART_IDS)[keyof typeof PART_IDS];

// ============================================
// Strand Configuration
// ============================================

/**
 * All strand container PartIds, ordered by strand number.
 * Each strand holds exactly one Mold position (position 1).
 */
export const STRAND_CONTAINER_IDS = [
  PART_IDS.STRAND_1,
  PART_IDS.STRAND_2,
  PART_IDS.STRAND_3,
  PART_IDS.STRAND_4,
  PART_IDS.STRAND_5,
] as const;

/**
 * Check if a PartId is a strand container
 */
export const isStrandContainer = (partId: number | undefined): boolean => {
  if (partId === undefined) return false;
  return (STRAND_CONTAINER_IDS as readonly number[]).includes(partId);
};

// ============================================
// Container Names for Display
// ============================================

export const CONTAINER_NAMES = {
  CASTER_ONLINE: "Caster-Online",
  CASTER_OFFLINE: "Caster-Offline",
  MOLD_SPARES: "Mold Spares",
  COPPER_MOLD_TUBE_SPARES: "Copper Mold Tube Spares",
  MOLD_BODY_SPARES: "Mold Body Spares",
  OIL_DISTRIBUTION_GASKET_SPARES: "Oil Distribution Gasket Spares",
  LEVEL_CONTROL_SPARES: "Level Control Spares",
  OIL_CONNECTOR_SPARES: "Oil Connector Spares",
  WATER_JACKET_FLUME_SPARES: "Water Jacket Flume Spares",
  UPPER_FLANGE_SPARES: "Upper Flange Spares",
  LOW_UPPER_FLANGE_SPARES: "Low Upper Flange Spares",
  LOWER_FLANGE_SPARES: "Lower Flange Spares",
  CLOSING_FLANGE_SPARES: "Closing Flange Spares",
  WATER_JACKET_FIXING_FLANGE_SPARES: "Water Jacket Fixing Flange Spares",
  UPPER_COLLAR_SPARES: "Upper Collar Spares",
  LOWER_COLLAR_SPARES: "Lower Collar Spares",
  UPPER_SEAL_SPARES: "Upper Seal Spares",
  COPPER_TUBE_SEAL_SPARES: "Copper Tube Seal Spares",
  WATER_JACKET_SEAL_SPARES: "Water Jacket Seal Spares",
  LOWER_INTERNAL_SEAL_SPARES: "Lower Internal Seal Spares",
  LOWER_EXTERNAL_SEAL_SPARES: "Lower External Seal Spares",
  OIL_CONNECTOR_ORING_SPARES: "Oil Connector O-Ring Spares",
  FOOT_ROLL_SPARES: "Foot Roll Spares",
  ROLL_SHAFT_SPARES: "Roll Shaft Spares",
  ROLLER_SUPPORT_SPARES: "Roller Support Spares",
  SHAFT_KEEPER_SPARES: "Shaft Keeper Spares",
  Z1_SPRAY_SPARES: "Z1 Spray Spares",
  SPRAY_NOZZLE_SPARES: "Spray Nozzle Spares",
} as const;

/**
 * Mapping from spares container PartId to display name
 * Uses Partial<Record<PartId, string>> since only some PartIds are spares containers
 */
export const SPARES_CONTAINER_NAMES: Partial<Record<PartId, string>> = {
  [PART_IDS.MOLD_SPARES]: CONTAINER_NAMES.MOLD_SPARES,
  [PART_IDS.COPPER_MOLD_TUBE_SPARES]: CONTAINER_NAMES.COPPER_MOLD_TUBE_SPARES,
  [PART_IDS.MOLD_BODY_SPARES]: CONTAINER_NAMES.MOLD_BODY_SPARES,
  [PART_IDS.OIL_DISTRIBUTION_GASKET_SPARES]:
    CONTAINER_NAMES.OIL_DISTRIBUTION_GASKET_SPARES,
  [PART_IDS.LEVEL_CONTROL_SPARES]: CONTAINER_NAMES.LEVEL_CONTROL_SPARES,
  [PART_IDS.OIL_CONNECTOR_SPARES]: CONTAINER_NAMES.OIL_CONNECTOR_SPARES,
  [PART_IDS.WATER_JACKET_FLUME_SPARES]:
    CONTAINER_NAMES.WATER_JACKET_FLUME_SPARES,
  [PART_IDS.UPPER_FLANGE_SPARES]: CONTAINER_NAMES.UPPER_FLANGE_SPARES,
  [PART_IDS.LOW_UPPER_FLANGE_SPARES]: CONTAINER_NAMES.LOW_UPPER_FLANGE_SPARES,
  [PART_IDS.LOWER_FLANGE_SPARES]: CONTAINER_NAMES.LOWER_FLANGE_SPARES,
  [PART_IDS.CLOSING_FLANGE_SPARES]: CONTAINER_NAMES.CLOSING_FLANGE_SPARES,
  [PART_IDS.WATER_JACKET_FIXING_FLANGE_SPARES]:
    CONTAINER_NAMES.WATER_JACKET_FIXING_FLANGE_SPARES,
  [PART_IDS.UPPER_COLLAR_SPARES]: CONTAINER_NAMES.UPPER_COLLAR_SPARES,
  [PART_IDS.LOWER_COLLAR_SPARES]: CONTAINER_NAMES.LOWER_COLLAR_SPARES,
  [PART_IDS.UPPER_SEAL_SPARES]: CONTAINER_NAMES.UPPER_SEAL_SPARES,
  [PART_IDS.COPPER_TUBE_SEAL_SPARES]: CONTAINER_NAMES.COPPER_TUBE_SEAL_SPARES,
  [PART_IDS.WATER_JACKET_SEAL_SPARES]: CONTAINER_NAMES.WATER_JACKET_SEAL_SPARES,
  [PART_IDS.LOWER_INTERNAL_SEAL_SPARES]:
    CONTAINER_NAMES.LOWER_INTERNAL_SEAL_SPARES,
  [PART_IDS.LOWER_EXTERNAL_SEAL_SPARES]:
    CONTAINER_NAMES.LOWER_EXTERNAL_SEAL_SPARES,
  [PART_IDS.OIL_CONNECTOR_ORING_SPARES]:
    CONTAINER_NAMES.OIL_CONNECTOR_ORING_SPARES,
  [PART_IDS.FOOT_ROLL_SPARES]: CONTAINER_NAMES.FOOT_ROLL_SPARES,
  [PART_IDS.ROLL_SHAFT_SPARES]: CONTAINER_NAMES.ROLL_SHAFT_SPARES,
  [PART_IDS.ROLLER_SUPPORT_SPARES]: CONTAINER_NAMES.ROLLER_SUPPORT_SPARES,
  [PART_IDS.SHAFT_KEEPER_SPARES]: CONTAINER_NAMES.SHAFT_KEEPER_SPARES,
  [PART_IDS.Z1_SPRAY_SPARES]: CONTAINER_NAMES.Z1_SPRAY_SPARES,
  [PART_IDS.SPRAY_NOZZLE_SPARES]: CONTAINER_NAMES.SPRAY_NOZZLE_SPARES,
};

/**
 * Ordered list of spares container IDs for consistent display
 * Order matches Caster-Offline children (seed-caster.sql positions 1-26)
 */
export const SPARES_CONTAINER_ORDER = [
  PART_IDS.MOLD_SPARES,
  PART_IDS.COPPER_MOLD_TUBE_SPARES,
  PART_IDS.MOLD_BODY_SPARES,
  PART_IDS.OIL_DISTRIBUTION_GASKET_SPARES,
  PART_IDS.LEVEL_CONTROL_SPARES,
  PART_IDS.OIL_CONNECTOR_SPARES,
  PART_IDS.WATER_JACKET_FLUME_SPARES,
  PART_IDS.UPPER_FLANGE_SPARES,
  PART_IDS.LOW_UPPER_FLANGE_SPARES,
  PART_IDS.LOWER_FLANGE_SPARES,
  PART_IDS.CLOSING_FLANGE_SPARES,
  PART_IDS.WATER_JACKET_FIXING_FLANGE_SPARES,
  PART_IDS.UPPER_COLLAR_SPARES,
  PART_IDS.LOWER_COLLAR_SPARES,
  PART_IDS.UPPER_SEAL_SPARES,
  PART_IDS.COPPER_TUBE_SEAL_SPARES,
  PART_IDS.WATER_JACKET_SEAL_SPARES,
  PART_IDS.LOWER_INTERNAL_SEAL_SPARES,
  PART_IDS.LOWER_EXTERNAL_SEAL_SPARES,
  PART_IDS.OIL_CONNECTOR_ORING_SPARES,
  PART_IDS.FOOT_ROLL_SPARES,
  PART_IDS.ROLL_SHAFT_SPARES,
  PART_IDS.ROLLER_SUPPORT_SPARES,
  PART_IDS.SHAFT_KEEPER_SPARES,
  PART_IDS.Z1_SPRAY_SPARES,
  PART_IDS.SPRAY_NOZZLE_SPARES,
] as const;

/**
 * Check if a container ID is a spare parts container
 */
export const isSpareContainer = (containerId: number | undefined): boolean => {
  if (containerId === undefined) return false;
  return (SPARES_CONTAINER_ORDER as readonly number[]).includes(containerId);
};

// ============================================
// Mapping from spares container to part type
// ============================================

export const SPARES_CONTAINER_TO_PART_TYPE: Partial<
  Record<PartId, PartTypeId>
> = {
  [PART_IDS.MOLD_SPARES]: PART_TYPE_IDS.MOLD,
  [PART_IDS.COPPER_MOLD_TUBE_SPARES]: PART_TYPE_IDS.COPPER_MOLD_TUBE,
  [PART_IDS.MOLD_BODY_SPARES]: PART_TYPE_IDS.MOLD_BODY,
  [PART_IDS.OIL_DISTRIBUTION_GASKET_SPARES]:
    PART_TYPE_IDS.OIL_DISTRIBUTION_GASKET,
  [PART_IDS.LEVEL_CONTROL_SPARES]: PART_TYPE_IDS.LEVEL_CONTROL,
  [PART_IDS.OIL_CONNECTOR_SPARES]: PART_TYPE_IDS.OIL_CONNECTOR,
  [PART_IDS.WATER_JACKET_FLUME_SPARES]: PART_TYPE_IDS.WATER_JACKET_FLUME,
  [PART_IDS.UPPER_FLANGE_SPARES]: PART_TYPE_IDS.UPPER_FLANGE,
  [PART_IDS.LOW_UPPER_FLANGE_SPARES]: PART_TYPE_IDS.LOW_UPPER_FLANGE,
  [PART_IDS.LOWER_FLANGE_SPARES]: PART_TYPE_IDS.LOWER_FLANGE,
  [PART_IDS.CLOSING_FLANGE_SPARES]: PART_TYPE_IDS.CLOSING_FLANGE,
  [PART_IDS.WATER_JACKET_FIXING_FLANGE_SPARES]:
    PART_TYPE_IDS.WATER_JACKET_FIXING_FLANGE,
  [PART_IDS.UPPER_COLLAR_SPARES]: PART_TYPE_IDS.UPPER_COLLAR,
  [PART_IDS.LOWER_COLLAR_SPARES]: PART_TYPE_IDS.LOWER_COLLAR,
  [PART_IDS.UPPER_SEAL_SPARES]: PART_TYPE_IDS.UPPER_SEAL,
  [PART_IDS.COPPER_TUBE_SEAL_SPARES]: PART_TYPE_IDS.COPPER_TUBE_SEAL,
  [PART_IDS.WATER_JACKET_SEAL_SPARES]: PART_TYPE_IDS.WATER_JACKET_SEAL,
  [PART_IDS.LOWER_INTERNAL_SEAL_SPARES]: PART_TYPE_IDS.LOWER_INTERNAL_SEAL,
  [PART_IDS.LOWER_EXTERNAL_SEAL_SPARES]: PART_TYPE_IDS.LOWER_EXTERNAL_SEAL,
  [PART_IDS.OIL_CONNECTOR_ORING_SPARES]: PART_TYPE_IDS.OIL_CONNECTOR_ORING,
  [PART_IDS.FOOT_ROLL_SPARES]: PART_TYPE_IDS.FOOT_ROLL,
  [PART_IDS.ROLL_SHAFT_SPARES]: PART_TYPE_IDS.ROLL_SHAFT,
  [PART_IDS.ROLLER_SUPPORT_SPARES]: PART_TYPE_IDS.ROLLER_SUPPORT,
  [PART_IDS.SHAFT_KEEPER_SPARES]: PART_TYPE_IDS.SHAFT_KEEPER,
  [PART_IDS.Z1_SPRAY_SPARES]: PART_TYPE_IDS.Z1_SPRAY,
  [PART_IDS.SPRAY_NOZZLE_SPARES]: PART_TYPE_IDS.SPRAY_NOZZLE,
};

// ============================================
// Position Names for Display
// ============================================

// ============================================
// Mold Hierarchy Configuration
// ============================================

/**
 * Group part type IDs (non-replaceable assemblies that hold child components)
 * These are container nodes in the tree, not leaf positions
 */
export const GROUP_TYPE_IDS = [
  PART_TYPE_IDS.FLANGES,
  PART_TYPE_IDS.COLLARS,
  PART_TYPE_IDS.SEALS,
  PART_TYPE_IDS.FOOT_ROLL_ASSEMBLY,
] as const;

/**
 * Check if a PartTypeId is a group type (non-replaceable assembly)
 */
export const isGroupType = (partTypeId: number | null | undefined): boolean => {
  if (partTypeId == null) return false;
  return (GROUP_TYPE_IDS as readonly number[]).includes(partTypeId);
};

/**
 * Foot Roll Assembly positions on Mold (positions 10-13)
 * Each is a separate instance of Foot-Roll-Assembly (PartTypeId 1013)
 */
export const FOOT_ROLL_POSITIONS = [
  { positionId: 10, label: "Foot Roll Left" },
  { positionId: 11, label: "Foot Roll Right" },
  { positionId: 12, label: "Foot Roll Internal" },
  { positionId: 13, label: "Foot Roll External" },
] as const;

/**
 * Check if a Mold position is a Foot Roll Assembly
 */
export const isFootRollPosition = (positionId: number): boolean => {
  return FOOT_ROLL_POSITIONS.some((fr) => fr.positionId === positionId);
};

/**
 * Check if a position is a direct child of Mold (standalone component, not in a group)
 * Positions 1-6 are standalone replaceable parts
 */
export const isMoldStandaloneChild = (positionId: number): boolean => {
  return positionId >= 1 && positionId <= 6;
};

/**
 * Check if a position is a group position on Mold (Flanges/Collars/Seals)
 * Positions 7-9 are group containers
 */
export const isMoldGroupChild = (positionId: number): boolean => {
  return positionId >= 7 && positionId <= 9;
};
